import React, { useMemo, useState, ReactNode, useRef, useCallback, ChangeEvent, Dispatch, SetStateAction, useEffect } from 'react';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    IconButton,
    Tabs,
    Tab,
    Typography, Paper,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    Divider,
    Stack,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    ButtonGroup,
    Alert
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { LoadingButton } from '@mui/lab';
import { Localized, useLocalization } from '@fluent/react';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import ChecklistIcon from "@mui/icons-material/Checklist";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';


import { TestPhase, TestPhaseStep } from '../../testSpecification/testSpecificationApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { SectionComments } from '../SectionComments';
import { docTestsResultsSelectors, docTestsRunsSelectors, docTestsSelectors, showDocComments, changeTestResult, changeTestRun, selectDocument, docDeviationsSelectors } from '../documentSlice';
import { RichTextEditor } from '../../../app/RichTextEditor';
import { store } from '../../../app/store';
import { impossible } from '../../../app/Utils';
import { DeleteButton } from '../../../app/components/DeleteButton';
import { APIError, AppTextFieldHandler } from '../../../app/appTypes';
import produce from "immer";
import { ServerErrorMsg } from '../../../app/ServerErrorMsg';
import { If } from '../../../app/If';
import { AssociateDeviationWithTest, CreateDeviationForm } from '../sections/deviationsSection/CreateDeviationForm';
import { DeviationsLoader } from '../sections/deviationsSection/DeviationsLoader';
import { DeviationsTable } from '../sections/deviationsSection/DeviationsTable';
import { PersonData, PersonalDataPicker, defPersonData } from '../PersonalDataPicker';
import { DocumentTest } from './documentTestsApi';
import { YesNoChip } from '../../../app/components/YesNoChip';

interface TestPreviewProps {
    test: DocumentTest
}


export const TestPreview = ({ test }: TestPreviewProps) => {
    const { l10n } = useLocalization();

    const [fullScreen, setFullScreen] = useState(true);

    const [mainTab, setMainTab] = useState(0);

    const testDefinition = test
    const spec = testDefinition.testSpecification

    const run = useAppSelector(state => testDefinition.lastRunId ? docTestsRunsSelectors.selectById(state, testDefinition.lastRunId) : undefined)

    const [tester, setTester] = useState<PersonData>(defPersonData)
    const [approver, setApprover] = useState<PersonData>(defPersonData)

    //TODO: legacy states - to delete
    const [testMetodology, setTestMetodology] = useState(spec.metodology ?? '');
    const [saving, setSaving] = useState(false);
    const [phaseTab, setPhaseTab] = useState(0);

    const testPhases = spec.testPhases

    const toggleFullScreen = () => setFullScreen((prev) => !prev);
    const handleMainTabChange = (_: React.SyntheticEvent, v: number) => setMainTab(v);
    const handlePhaseTabChange = (_: React.SyntheticEvent, v: number) => setPhaseTab(v);

    let content = <></>
    if (testPhases.length === 0) {
        content = <Typography variant="body2" color="text.secondary">Brak faz testu do edycji.</Typography>
    } else {
        content = <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1 }}>
            <Box display="flex" flexDirection="column" gap={3} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <TextField
                    label={<Localized id="test-name">_test-name</Localized>}
                    value={spec.name}
                    sx={{ mt: '10px' }}
                    fullWidth
                    inputProps={{
                        'readonly': 'true'
                    }}
                />

                <Divider />

                <Box sx={{
                    display: 'flex',
                    gap: 4,
                }}>
                    <FormControl component="fieldset" variant="standard" required error={run?.outcome === 'UNKNOWN'}>
                        <FormLabel component="legend">Wynik testu</FormLabel>
                        <YesNoChip value={run?.outcome === 'PASSED'} />
                    </FormControl>
                    <TextField
                        sx={{
                            flexGrow: 1,
                        }}
                        label={'Komentarz'}
                        value={run?.comment ?? ''}
                        multiline
                        rows={3}
                    />
                </Box>

                <Tabs value={mainTab} onChange={handleMainTabChange} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={<Localized id="test-procedure">
                        <span>_przebieg testu</span>
                    </Localized>} />
                    <Tab label={<Localized id="description">
                        <span>_description</span>
                    </Localized>} />
                    <Tab label={<Localized id="test-methodology">
                        <span>_metodologia testu</span>
                    </Localized>} />
                    <Tab label={<Localized id="test-participants">
                        <span>_Uczestnicy</span>
                    </Localized>} />
                </Tabs>

                {mainTab === 0 && run && (
                    <>
                        <Tabs value={phaseTab} onChange={handlePhaseTabChange} variant="scrollable" allowScrollButtonsMobile>
                            {testPhases.map((phase) => (
                                <Tab
                                    key={phase.id}
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <span>{phase.phaseType}</span>
                                            {!phase.isActive && (<RemoveCircleOutlineIcon color="error" fontSize="small" />)}
                                            {phase.isActive && (<CheckCircleOutlineIcon color="success" fontSize="small" />)}
                                        </Box>
                                    }
                                />
                            ))}
                        </Tabs>

                        {testPhases.map((phase, index) =>
                            phaseTab === index
                                ? <TestPhaseResult
                                    key={phase.id}
                                    phase={phase}
                                    runId={run.id}
                                    testId={test.id}
                                />
                                : null)}
                    </>
                )}

                {mainTab === 1 && (
                    <Box>
                        <Paper variant="outlined">
                            <Box
                                px={1.5}
                                py={1}
                                bgcolor={(t) => t.palette.grey[100]}
                                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                            >
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Opis testu
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2 }}>
                                <RichTextEditor
                                    label={l10n.getString("test-description", null, "test-description")}
                                    value={spec.description}
                                    editable={false}
                                    onValueChange={() => {}}
                                />
                            </Box>
                        </Paper>

                    </Box>
                )}
                {mainTab === 2 && (
                    <Box>
                        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
                            <Box
                                px={1.5}
                                py={1}
                                bgcolor={(t) => t.palette.grey[100]}
                                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                            >
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Metodologia
                                </Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                                <RichTextEditor
                                    label={l10n.getString("test-metodology", null, "test-metodology")}
                                    value={testMetodology}
                                    editable={false}
                                    onValueChange={setTestMetodology} />
                            </Box>

                        </Paper>

                    </Box>
                )}
                {mainTab === 3 && <ParticipantsForm tester={tester} setTester={setTester} approver={approver} setApprover={setApprover} />}
            </Box>
        </Box>
    }

    return content
};

interface TestPhaseResultProps {
    phase: TestPhase;
    runId: number
    testId: number
}
const TestPhaseResult = (props: TestPhaseResultProps) => {
    const { phase, runId, testId } = props
  const initialSteps = phase?.steps ?? [];
  const [steps, setSteps] = useState<TestPhaseStep[]>(initialSteps);

  const [selectedStepId, setSelectedStepId] = useState<number | null>(steps[0]?.id ?? null);

  const newStepRef = useRef<HTMLInputElement | null>(null);
  const newStepId = useRef<number | null>(null);

  const selectedStep = useMemo(() => steps.find((s) => s.id === selectedStepId) ?? null, [steps, selectedStepId]);

  const handleStepNameChange = (value: string, stepId: number) => {
    const updated = steps.map((s) => (s.id === stepId ? { ...s, name: value } : s));
    setSteps(updated);
  };

  return (<Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    }}>
    <Paper >
      <Box p={2}>
        {/* <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}> */}
          {/* <TestSubTypeEditor subType={selectedSubType} onUpdatedSubType={handleSubTypeChange} /> */}
        {/* </Box> */}

        <Grid container spacing={2} sx={{ height: '100%' }}>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
                <Typography variant="body1" fontWeight={700}>
                  {phase.phaseType} — {<Localized id="test-steps">
                    <span>_test-steps</span>
                  </Localized>} ({steps.length})
                </Typography>
              </Box>
              <Box px={1.5} py={0.5} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={2}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="lp">
                        <span>_Lp.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="name">
                        <span>_name.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="validation-acceptance-criteria">
                        <span>_Kryteria.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <List dense disablePadding>
                {steps.map((step) => (
                  <ListItem key={step.id} disableGutters sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                    <ListItemButton
                      selected={selectedStepId === step.id}
                      onClick={() => setSelectedStepId(step.id)}
                      sx={{ py: 1, px: 1.5 }}
                    >
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={2}>
                          <Typography variant="body2" color="text.secondary">{step.order}.</Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" noWrap>
                                {step.name || <em style={{ color: '#888' }}>
                                  <Localized id="test-no-step-description-warning">
                                    <span>_Brak opisu…</span>
                                  </Localized>
                                </em>}
                              </Typography>
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Chip size="small" icon={<ChecklistIcon />} label={String(step.criterias.length)} />
                        </Grid>
                      </Grid>
                    </ListItemButton>
                  </ListItem>
                ))}
                {steps.length === 0 && (
                  <Box p={2} textAlign="center" color="text.secondary">

                    <Localized id="test-no-steps-warning">
                      <span>_ Brak kroków — dodaj pierwszy.</span>
                    </Localized>
                  </Box>
                )}
              </List>

            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" >
              <Box p={2}>
                {!selectedStep && (
                  <Typography color="text.secondary"
                  >
                    <Localized id="test-choose-step-to-edit">
                      <span>_Wybierz krok po lewej, aby edytować.</span>
                    </Localized>
                  </Typography>
                )}
                {selectedStep && (
                  <Stack spacing={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        noWrap
                        sx={{ width: 80 }}
                      >
                        <Localized id="test-step">
                          <span>_step.</span>
                        </Localized>
                        {selectedStep.order}
                      </Typography>
                      <TextField
                        inputProps={{ 'readonly': 'true' }}
                        label={<Localized id="test-step-description"></Localized>}
                        value={selectedStep.name}
                        onChange={(e) => handleStepNameChange(e.target.value, selectedStep.id)}
                        fullWidth
                        inputRef={newStepId.current === selectedStep.id ? newStepRef : undefined}
                      />
                    </Box>

                    <Divider />

                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2">
                          <Localized id="validation-acceptance-criteria">
                            <span>_Kryteria.</span>
                          </Localized>
                          ({selectedStep.criterias.length})</Typography>
                      </Box>

                      <Stack spacing={1.25}>
                        {selectedStep.criterias.map((c) => (
                          <Box
                            key={c.id}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
                          >
                            <Typography sx={{
                                alignSelf: 'flex-start',
                            }} variant="body2" width={44} textAlign="right" color="text.secondary">
                              {selectedStep.order}.{c.order}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                flexGrow: 1,
                            }}>
                                <Typography bgcolor={(t) => t.palette.grey[100]} sx={{ px: 1 }}>{c.name}</Typography>
                                <Box sx={{
                                    mt: .5,
                                    display: 'flex',
                                    gap: 2,
                                }}>
                                    <TestCriteriaResultWrapper criteriaId={c.id} runId={runId} testId={testId} />
                                </Box>
                            </Box>
                          </Box>
                        ))}
                        {selectedStep.criterias.length === 0 && (
                          <Typography color="text.secondary" variant="body2">
                            <Localized id="test-no-criteria-warning">
                              <span>_Brak kryteriów — dodaj pierwsze.</span>
                            </Localized>
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  </Box>);
}

export const TestCriteriaResultWrapper = (props: { criteriaId: number, runId: number, testId: number }) => {
    const { criteriaId, runId, testId } = props

    const resultId = useMemo(() => {
        return docTestsResultsSelectors.selectAll(store.getState()).find(x => x.criteriaId === criteriaId && x.runId === runId)?.id
    }, [criteriaId, runId])

    if (resultId) {
        return <TestCriteriaResultForm resultId={resultId} testId={testId} />
    }

    return <span>Cannot find result entity</span>
}

export const TestCriteriaResultForm = (props: { resultId: number, testId: number }) => {
    const { resultId, testId } = props

    const result = useAppSelector(state => docTestsResultsSelectors.selectById(state, resultId)) ?? impossible()

    const testDefinition = useAppSelector(state => docTestsSelectors.selectById(state, testId)) ?? impossible()

    return <>
        <TextField sx={{
            flexGrow: 1,
        }}
            multiline
            label={'Komentarz'}
            size='small'
            rows={2}
            value={result.comment}
            placeholder="Dodatkowy komentarz..."
            inputProps={{
                'readonly': 'true'
            }}
        />
        <FormControl sx={{ m: 0 }} component="fieldset" variant="standard" required error={!result.passed}>
            <FormLabel component="legend">Spełnia</FormLabel>
            <YesNoChip value={result.passed} />
        </FormControl>
    </>
}

const rememberKey = 'RunTestActionRememberFlag'
const participantsKey = 'RunTestActionDefaultParticipants'

type ParticipantsData =
    { v: '1'
    , testerUserName: string | null
    , testerName: string
    , testerPosition: string
    , testerEmail: string
    , testerCompanyName: string
    , approverUserName: string | null
    , approverName: string
    , approverPosition: string
    , approverEmail: string
    , approverCompanyName: string
    }

function serialize(tester: PersonData, approver: PersonData): string {
    const data: ParticipantsData = {
        v: "1",
        testerUserName: tester.userName,
        testerName: tester.fullName,
        testerPosition: tester.position,
        testerEmail: tester.email,
        testerCompanyName: tester.companyName,
        approverUserName: approver.userName,
        approverName: approver.fullName,
        approverPosition: approver.position,
        approverEmail: approver.email,
        approverCompanyName: approver.companyName,
    }
    return JSON.stringify(data)
}

function deserialize(data: string): { tester: PersonData, approver: PersonData } {
    const obj: ParticipantsData = JSON.parse(data)
    return {
        tester: {
            userName: obj.testerUserName ?? '',
            fullName: obj.testerName,
            position: obj.testerPosition,
            email: obj.testerEmail,
            companyName: obj.testerCompanyName,
        },
        approver: {
            userName: obj.approverUserName ?? '',
            fullName: obj.approverName,
            position: obj.approverPosition,
            email: obj.approverEmail,
            companyName: obj.approverCompanyName,
        },
    }
}


function * allTestResultIds(test: DocumentTest, runId: number) {
    for (let i = 0; i < test.runs.length; i++) {
        const run = test.runs[i];
        if (runId !== run.id) continue
        for (let j = 0; j < run.criteriaResults.length; j++) {
            const cr = run.criteriaResults[j];
            yield cr.id
        }
    }
}

interface ParticipantsFormProps {
    tester: PersonData
    setTester: Dispatch<SetStateAction<PersonData>>
    approver: PersonData
    setApprover: Dispatch<SetStateAction<PersonData>>
}
const ParticipantsForm = (props: ParticipantsFormProps) => {
    const { tester, setTester, approver, setApprover } = props

    return <Box>
        <Grid container spacing={1}>
            <Grid item xs={6}>
                <Paper>
                    <PersonalDataPicker styles={{
                        mt: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }} data={tester} setData={setTester} label={<>Tester</>} />
                </Paper>
            </Grid>
            <Grid item xs={6}>
                <Paper>
                    <PersonalDataPicker styles={{
                        mt: 1,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }} data={approver} setData={setApprover} label={<>Tester</>} />
                </Paper>
            </Grid>
        </Grid>
    </Box>
}
